import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { t } from 'i18next';
import { MathUtils, Vector2 } from 'three';
import { Rectangle } from '@thive/fddm-types';

import { Accordion } from '@/modules/common/components/Accordion';
import { Dimensions } from '@/modules/common/components/Dimensions';
import { clampAngle180 } from '@/modules/common/helpers/math';
import { NumUtils } from '@/modules/common/utils';
import { offsetSegments } from '@/modules/floorplanDesign/helpers/helpers';
import { RectangleUtils } from '@/modules/floorplanDesign/helpers/RectangleUtils';
import { minItemsArrayUtils } from '@/modules/floorplanDesign/helpers/minItems';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';

type FloorplanDesignState = ReturnType<typeof useFloorplanDesignStore.getState>;

const getSelectedObstacleBounds = (
  selectedIds: string[],
  floorplanState: FloorplanDesignState,
): Rectangle | null => {
  const selectedObstacles = floorplanState.getObstaclesByIds(selectedIds).map(o => o.segments);

  if (selectedObstacles.length === 0) return null;
  if (selectedObstacles.length > 1) return null;
  if (selectedObstacles[0].length !== 1) return null;

  return RectangleUtils.fromSegment(selectedObstacles[0][0]);
};

const useSelectedObstacleDimension = (
  selectedIds: string[],
  value: keyof Rectangle,
): number | null =>
  useFloorplanDesignStore((state) => {
    const bounds = getSelectedObstacleBounds(selectedIds, state);
    return bounds ? bounds[value] : null;
  });

const useObstacleDimensions = () => {
  const selectedIds = useAppStore((state) => state.selectedIds);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const centerX = useSelectedObstacleDimension(selectedIds, 'centerX');
  const centerY = useSelectedObstacleDimension(selectedIds, 'centerY');
  const length = useSelectedObstacleDimension(selectedIds, 'length');
  const width = useSelectedObstacleDimension(selectedIds, 'width');
  const angle = useSelectedObstacleDimension(selectedIds, 'angle');

  const dimensions = useMemo(
    () =>
      centerX === null || centerY === null || length === null || width === null || angle === null
        ? null
        : { centerX, centerY, length, width, angle: MathUtils.radToDeg(angle) },
    [centerX, centerY, length, width, angle],
  );

  const currentDimensionsRef = useRef<Rectangle | null>(null);

  useEffect(() => {
    currentDimensionsRef.current = dimensions;
  }, [dimensions]);

  const getSelectedObstacles = () => {
    const floorplanState = useFloorplanDesignStore.getState();
    const currentSelectedIds = useAppStore.getState().selectedIds;
    return floorplanState.getObstaclesByIds(currentSelectedIds);
  };

  const isSingleResizableObstacle = useFloorplanDesignStore((state) => {
    const selectedObstacles = state.getObstaclesByIds(selectedIds);
    return (
      selectedObstacles.length === 1 &&
      selectedObstacles[0].appearance !== 'WALL' &&
      selectedObstacles[0].segments.length === 1
    );
  });

  const onXChange = useCallback(
    (newValue: number) => {
      if (!currentDimensionsRef.current) return;

      const deltaX = NumUtils.mmToMetersRoundedToMm(newValue) - currentDimensionsRef.current.centerX;
      if (deltaX === 0) return;

      getSelectedObstacles().forEach((obstacle) => {
        fddmSvc.updateObstacle({
          ...obstacle,
          segments: offsetSegments(obstacle.segments, new Vector2(deltaX, 0)),
        });
      });
    },
    [fddmSvc],
  );

  const onYChange = useCallback(
    (newValue: number) => {
      if (!currentDimensionsRef.current) return;

      const deltaY = NumUtils.mmToMetersRoundedToMm(newValue) - currentDimensionsRef.current.centerY;
      if (deltaY === 0) return;

      getSelectedObstacles().forEach((obstacle) => {
        fddmSvc.updateObstacle({
          ...obstacle,
          segments: offsetSegments(obstacle.segments, new Vector2(0, deltaY)),
        });
      });
    },
    [fddmSvc],
  );

  const onLengthChange = useCallback(
    (newValue: number) => {
      if (!isSingleResizableObstacle) return;

      const obstacle = getSelectedObstacles()[0];
      const rectangle = RectangleUtils.fromSegment(obstacle.segments[0]);
      const updatedSegment = RectangleUtils.toPartialSegment({
        ...rectangle,
        length: NumUtils.mmToMetersRoundedToMm(newValue),
      });

      fddmSvc.updateObstacle({
        ...obstacle,
        segments: minItemsArrayUtils.mapAtLeast1(obstacle.segments, (segment, index) =>
          index === 0 ? { ...segment, ...updatedSegment } : segment,
        ),
      });
    },
    [fddmSvc, isSingleResizableObstacle],
  );

  const onWidthChange = useCallback(
    (newValue: number) => {
      if (!isSingleResizableObstacle) return;

      const obstacle = getSelectedObstacles()[0];
      const rectangle = RectangleUtils.fromSegment(obstacle.segments[0]);
      const updatedSegment = RectangleUtils.toPartialSegment({
        ...rectangle,
        width: NumUtils.mmToMetersRoundedToMm(newValue),
      });

      fddmSvc.updateObstacle({
        ...obstacle,
        segments: minItemsArrayUtils.mapAtLeast1(obstacle.segments, (segment, index) =>
          index === 0 ? { ...segment, ...updatedSegment } : segment,
        ),
      });
    },
    [fddmSvc, isSingleResizableObstacle],
  );

  const onAngleChange = useCallback(
    (newValue: number) => {
      if (!isSingleResizableObstacle) return;

      const obstacle = getSelectedObstacles()[0];
      const rectangle = RectangleUtils.fromSegment(obstacle.segments[0]);
      const updatedSegment = RectangleUtils.toPartialSegment({
        ...rectangle,
        angle: MathUtils.degToRad(clampAngle180(newValue)),
      });

      fddmSvc.updateObstacle({
        ...obstacle,
        segments: minItemsArrayUtils.mapAtLeast1(obstacle.segments, (segment, index) =>
          index === 0 ? { ...segment, ...updatedSegment } : segment,
        ),
      });
    },
    [fddmSvc, isSingleResizableObstacle],
  );

  return {
    dimensions,
    onXChange,
    onYChange,
    onLengthChange,
    onWidthChange,
    onAngleChange,
    isGeometryEditable: isSingleResizableObstacle,
  };
};

function ObstacleDimensionsComponent() {
  const {
    dimensions,
    onXChange,
    onYChange,
    onLengthChange,
    onWidthChange,
    onAngleChange,
    isGeometryEditable,
  } = useObstacleDimensions();

  const mode = useAppStore((state) => state.mode);
  const disabled = mode !== 'EDITABLE';
  
  if (!dimensions) return null;

  return (
    <Accordion
      title={t<string>('interface:properties.dimensions.label', 'Dimensions')}
      unmountOnExit
      defaultExpanded
      sx={{ px: 2 }}
    >
      <Dimensions
        disabled={disabled}
        disableLength={!isGeometryEditable}
        disableWidth={!isGeometryEditable}
        disableAngle={!isGeometryEditable}
        x={NumUtils.metersToMmRounded(dimensions.centerX)}
        onXChange={onXChange}
        y={NumUtils.metersToMmRounded(dimensions.centerY)}
        onYChange={onYChange}
        length={NumUtils.metersToMmRounded(dimensions.length)}
        onLengthChange={onLengthChange}
        width={NumUtils.metersToMmRounded(dimensions.width)}
        onWidthChange={onWidthChange}
        angle={dimensions.angle}
        onAngleChange={onAngleChange}
      />
    </Accordion>
  );
}

export const ObstacleDimensions = memo(ObstacleDimensionsComponent);